/**
 * Feature E07-F04 — Review screen pure model/logic (web side).
 *
 * Framework-free, side-effect-free helpers ported 1:1 from the prototype
 * `../EER app/prototype/assets/research.proto.js` (filteredCompanies L857-870,
 * gradeChip L820-828, verdictChip L840-847, renderReview summary strip L872-938).
 * Keeping the rules here makes the score-monotonic displayRank, the grade/status
 * filter combination, the count badges and the chip palettes unit-testable
 * without a DOM — exactly like researchLibraryModel.ts.
 *
 * Parity anchors (research.proto.js):
 *   - filteredCompanies: FULL-list sort by score (null → -1, DESC default / ASC
 *     toggle), THEN `displayRank = i+1`, THEN grade & status filters — so the #
 *     stays score-monotonic 1..N independent of the active filter and manual
 *     unscored rows sink to the bottom (this is the T08 rule the T05 table depends on);
 *   - gradeChip: A green / B blue / C gray; null → "—" pill;
 *   - verdictChip: pass green / partial amber / fail red;
 *   - summary strip: total on long-list · accepted · needs-human ·
 *     "showing X of total (N discovered)".
 */
import {
  REVIEW_CRITERIA_LEGEND,
  INSUFFICIENT_DATA_LABEL,
  INSUFFICIENT_DATA_SHORT,
  describeInsufficientData,
  discoveryTileLabel,
  formatCityState,
  type ReviewRow,
  type ReviewGrade,
  type ReviewStoredVerdict,
  type ReviewGradeFilter,
  type ReviewStatusFilter,
  type ReviewSortDir,
  type ReviewCriterionKey,
  type GradeScope,
} from '@eer/shared';
// E07-F11-T02 — Jev triage buckets: filter + «check first» order.
import {
  hasTriage,
  matchesTriageFilter,
  triageBucketRank,
  type ReviewTriageFilter,
} from './researchTriageModel.js';

// ---------------------------------------------------------------------------
// T05 / T08 — score-monotonic displayRank (full-list sort BEFORE filtering)
// ---------------------------------------------------------------------------

/**
 * Sort the FULL list by score so the displayed rank (#) is score-monotonic, then
 * stamp `displayRank = i+1`. Unscored rows (score=null) sink to the bottom under
 * BOTH directions (sort key is the tuple (score===null, score)).
 * DESC by default; `sortDir='asc'` flips the order. Returns a NEW array of rows
 * with `displayRank` populated (the input is not mutated).
 *
 * E07-F04-T13 — reference companies (`isReference`, the intake competitors) form a
 * block at the TOP of the list, score-sorted within it: an AI check changes their
 * score but never throws them to the other end of the table. Equal scores tie-break
 * on the row id, so the order never depends on the order the API happened to return.
 */
export function rankByScore(
  rows: readonly ReviewRow[],
  sortDir: ReviewSortDir = 'desc',
): ReviewRow[] {
  const sorted = [...rows].sort((a, b) => {
    const ref = Number(Boolean(b.isReference)) - Number(Boolean(a.isReference));
    if (ref !== 0) return ref;
    // E07-F11-T01 — rows a stage-0 prefilter marked sit at the END of the list by
    // default (spec §5.1): visible, never removed, out of the way.
    const flagged = Number(isPrefiltered(a)) - Number(isPrefiltered(b));
    if (flagged !== 0) return flagged;
    // Unscored rows form their own partition that ALWAYS sorts after the scored
    // one — regardless of sortDir (a `?? -1` sentinel would float them to the
    // top under ASC).
    const aNull = a.score === null || a.score === undefined;
    const bNull = b.score === null || b.score === undefined;
    if (aNull !== bNull) return aNull ? 1 : -1;
    const av = a.score ?? 0;
    const bv = b.score ?? 0;
    const byScore = sortDir === 'desc' ? bv - av : av - bv;
    return byScore !== 0 ? byScore : a.id - b.id;
  });
  return sorted.map((row, i) => ({ ...row, displayRank: i + 1 }));
}

// ---------------------------------------------------------------------------
// E07-F11-T01 — stage-0 prefilter flags (spec §5.1: a mark with a reason, never a cut)
// ---------------------------------------------------------------------------

/** "all" = every row; "flagged" = only the rows a prefilter marked. */
export type ReviewPrefilterFilter = 'all' | 'flagged';

/** Does a prefilter flag sit on the row (a returned flag is no longer listed)? Pure. */
export function isPrefiltered(row: Pick<ReviewRow, 'prefilterFlags'>): boolean {
  return (row.prefilterFlags ?? []).length > 0;
}

/** How many rows of the FULL list carry a prefilter flag (the filter pill count). Pure. */
export function prefilteredCount(rows: readonly Pick<ReviewRow, 'prefilterFlags'>[]): number {
  return rows.filter(isPrefiltered).length;
}

// ---------------------------------------------------------------------------
// E07-F04-T13 — company location cells (country / city / HQ country)
// ---------------------------------------------------------------------------

export interface LocationCells {
  country: string;
  city: string;
  hqCountry: string;
}

/**
 * The three location cells of a Review row: what the company-data search reported,
 * "—" for a field it did not return (never a guess). Pure.
 */
export function locationCells(row: Pick<ReviewRow, 'location'>): LocationCells {
  const loc = row.location ?? null;
  const show = (v: string | null | undefined): string => (v && v.trim() ? v.trim() : '—');
  // E12-F17-T02 — City = "<city>, <state>", the same shared format as the export.
  return {
    country: show(loc?.country),
    city: formatCityState(loc) ?? '—',
    hqCountry: show(loc?.hqCountry),
  };
}

// ---------------------------------------------------------------------------
// E07-F04-T14 — tile / industry cells + facets (filter options, sort keys)
// ---------------------------------------------------------------------------

/** The two row facets Review filters and sorts by (besides grade / status / score). */
export type ReviewFacet = 'tile' | 'industry';

/**
 * What the table sorts by: the score (default, reference block on top), a facet, or (E07-F11-T02)
 * the Jev triage bucket — «check first» on top.
 */
export type ReviewSortBy = 'score' | ReviewFacet | 'triage';

/** Facet filter value meaning "no filter". */
export const REVIEW_FACET_ALL = 'all';
/** Facet filter value for rows without a tile / industry (seed, manual, not reported). */
export const REVIEW_FACET_NONE = '__none__';
/** What a row without a tile / industry shows (never a guess). */
export const REVIEW_FACET_EMPTY = '—';

/**
 * The value a row shows for a facet: the tile as "sub-industry · geo · size"
 * (the same label the discovery warning uses) or the reported industry; "—" when
 * the row has none (reference seed, manual add, provider did not report it). Pure.
 */
export function facetLabel(row: Pick<ReviewRow, 'tile' | 'industry'>, facet: ReviewFacet): string {
  if (facet === 'tile') return row.tile ? discoveryTileLabel(row.tile) : REVIEW_FACET_EMPTY;
  const industry = row.industry?.trim();
  return industry ? industry : REVIEW_FACET_EMPTY;
}

/** The filter key of a row for a facet: its label, or REVIEW_FACET_NONE. Pure. */
export function facetKey(row: Pick<ReviewRow, 'tile' | 'industry'>, facet: ReviewFacet): string {
  const label = facetLabel(row, facet);
  return label === REVIEW_FACET_EMPTY ? REVIEW_FACET_NONE : label;
}

export interface FacetOption {
  value: string;
  label: string;
  count: number;
}

/**
 * The filter options of a facet over the FULL list: every distinct value with its
 * row count, alphabetical, the "none" bucket last (only when some row has none).
 * Pure.
 */
export function facetOptions(rows: readonly ReviewRow[], facet: ReviewFacet): FacetOption[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const key = facetKey(row, facet);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const none = counts.get(REVIEW_FACET_NONE) ?? 0;
  counts.delete(REVIEW_FACET_NONE);
  const options = [...counts.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([value, count]) => ({ value, label: value, count }));
  if (none > 0) {
    options.push({
      value: REVIEW_FACET_NONE,
      label: facet === 'tile' ? 'No tile' : 'Not reported',
      count: none,
    });
  }
  return options;
}

// ---------------------------------------------------------------------------
// E07-F01-T11 — "insufficient data" badge (no evidence ≠ checked and unfit)
// ---------------------------------------------------------------------------

export interface InsufficientDataBadge {
  /** "Insufficient data". */
  label: string;
  /** Short visible reason ("website did not open" / "no text on the website" / ...). */
  reason: string;
  /** Full sentence (+ why the site did not open) for the tooltip. */
  title: string;
}

/**
 * The Review-row badge for a row whose check found nothing to judge by, or null.
 * Such a row has no score and no automatic grade — the badge says WHY. Pure.
 */
export function insufficientDataBadge(
  row: Pick<ReviewRow, 'insufficientData'>,
): InsufficientDataBadge | null {
  const d = row.insufficientData;
  if (!d) return null;
  return {
    label: INSUFFICIENT_DATA_LABEL,
    reason: INSUFFICIENT_DATA_SHORT[d.reason],
    title: describeInsufficientData(d),
  };
}

// ---------------------------------------------------------------------------
// T05 — grade + status filters (combine; applied AFTER ranking)
// ---------------------------------------------------------------------------

export interface ReviewFilterState {
  grade: ReviewGradeFilter;
  /**
   * E07-F04-T18 — the request's Grade scope; read by `grade: 'scope'`. Unknown
   * (undefined) → 'scope' keeps every graded row (A+B+C).
   */
  gradeScope?: GradeScope;
  status: ReviewStatusFilter;
  sortDir: ReviewSortDir;
  /** E07-F04-T14 — sort key; optional so older callers keep the score sort. */
  sortBy?: ReviewSortBy;
  /** E07-F04-T14 — tile filter: REVIEW_FACET_ALL, a tile label or REVIEW_FACET_NONE. */
  tile?: string;
  /** E07-F04-T14 — industry filter: REVIEW_FACET_ALL, an industry or REVIEW_FACET_NONE. */
  industry?: string;
  /** E07-F11-T01 — "flagged" keeps only rows a stage-0 prefilter marked. */
  prefilter?: ReviewPrefilterFilter;
  /** E07-F11-T02 — triage bucket filter (all / one bucket / not triaged). */
  triage?: ReviewTriageFilter;
}

export const DEFAULT_REVIEW_FILTER: ReviewFilterState = {
  grade: 'all',
  status: 'all',
  sortDir: 'desc',
  sortBy: 'score',
  tile: REVIEW_FACET_ALL,
  industry: REVIEW_FACET_ALL,
};

// ---------------------------------------------------------------------------
// E07-F04-T18 — Grade scope of the request drives the default grade filter
// ---------------------------------------------------------------------------

/** The grades inside a Grade scope: 'A' → A; 'A+B' → A, B; 'A+B+C' → A, B, C. Pure. */
export function scopeGrades(scope: GradeScope | undefined): readonly ReviewGrade[] {
  if (scope === 'A') return ['A'];
  if (scope === 'A+B') return ['A', 'B'];
  return ['A', 'B', 'C'];
}

/**
 * The filter Review opens with: the grade filter follows the request's Grade scope
 * (BEN #141 — scope A must not open on all rows incl. the ungraded ones). Without
 * a known scope the list opens unfiltered, as before. Pure.
 */
export function initialReviewFilter(scope: GradeScope | undefined): ReviewFilterState {
  if (!scope) return DEFAULT_REVIEW_FILTER;
  return { ...DEFAULT_REVIEW_FILTER, grade: 'scope', gradeScope: scope };
}

/** Does a row pass the grade filter? Pure. */
export function matchesGradeFilter(
  row: Pick<ReviewRow, 'grade'>,
  grade: ReviewGradeFilter,
  scope: GradeScope | undefined,
): boolean {
  if (grade === 'all') return true;
  if (grade === 'ungraded') return !row.grade;
  if (grade === 'scope') return Boolean(row.grade) && scopeGrades(scope).includes(row.grade!);
  return row.grade === grade;
}

export interface UngradedNotice {
  /** Rows with no grade (any reason). */
  ungraded: number;
  /** Of them: rows no AI check / Verify has touched yet (no score). */
  notChecked: number;
  /** Of the ungraded: how many the active grade filter hides. */
  hidden: number;
}

/**
 * E07-F04-T18 — the rows without a grade, so the screen can say they have not
 * passed the AI check yet (and how many the scope filter hides). Full list. Pure.
 */
export function ungradedNotice(
  rows: readonly ReviewRow[],
  filter: Pick<ReviewFilterState, 'grade' | 'gradeScope'>,
): UngradedNotice {
  const ungradedRows = rows.filter((r) => !r.grade);
  return {
    ungraded: ungradedRows.length,
    notChecked: ungradedRows.filter((r) => r.checkState === 'not_checked').length,
    hidden: ungradedRows.filter((r) => !matchesGradeFilter(r, filter.grade, filter.gradeScope))
      .length,
  };
}

/**
 * E07-F04-T14 — click on a sort control: the same key flips the direction; a new key
 * starts at its natural direction (score high→low, tile / industry A→Z). Pure.
 */
export function nextSort(filter: ReviewFilterState, by: ReviewSortBy): ReviewFilterState {
  const current = filter.sortBy ?? 'score';
  if (current === by) return { ...filter, sortDir: toggleSortDir(filter.sortDir) };
  return { ...filter, sortBy: by, sortDir: by === 'score' ? 'desc' : 'asc' };
}

/**
 * Order ranked rows by a facet: label A→Z (or Z→A), rows without a value LAST in
 * either direction, then by the score rank so a tile's best companies come first.
 */
function sortByFacet(rows: ReviewRow[], facet: ReviewFacet, dir: ReviewSortDir): ReviewRow[] {
  return [...rows].sort((a, b) => {
    const ak = facetKey(a, facet);
    const bk = facetKey(b, facet);
    const aNone = ak === REVIEW_FACET_NONE;
    const bNone = bk === REVIEW_FACET_NONE;
    if (aNone !== bNone) return aNone ? 1 : -1;
    const byLabel = aNone ? 0 : ak.localeCompare(bk);
    if (byLabel !== 0) return dir === 'asc' ? byLabel : -byLabel;
    return (a.displayRank ?? 0) - (b.displayRank ?? 0);
  });
}

/**
 * Rank the full list (score-monotonic) THEN apply the grade AND status filters.
 * The two filters COMBINE (grade==A && status==accepted). Because ranking runs on
 * the full list first, the surviving rows keep their full-list displayRank — the
 * # never "jumps" when a filter narrows the view (T05/T08 acceptance).
 */
export function filteredRankedRows(
  rows: readonly ReviewRow[],
  filter: ReviewFilterState,
): ReviewRow[] {
  const sortBy = filter.sortBy ?? 'score';
  // E07-F04-T14 — the # stays the SCORE rank (desc) when a facet drives the order,
  // so "#3 in the Pumps tile" still means the third-best company overall.
  const ranked = rankByScore(rows, sortBy === 'score' ? filter.sortDir : 'desc');
  const tile = filter.tile ?? REVIEW_FACET_ALL;
  const industry = filter.industry ?? REVIEW_FACET_ALL;
  const kept = ranked.filter((row) => {
    if (!matchesGradeFilter(row, filter.grade, filter.gradeScope)) return false;
    if (filter.status !== 'all' && row.reviewStatus !== filter.status) return false;
    if (tile !== REVIEW_FACET_ALL && facetKey(row, 'tile') !== tile) return false;
    if (industry !== REVIEW_FACET_ALL && facetKey(row, 'industry') !== industry) return false;
    if (filter.prefilter === 'flagged' && !isPrefiltered(row)) return false;
    if (!matchesTriageFilter(row, filter.triage)) return false;
    return true;
  });
  if (sortBy === 'score') return kept;
  if (sortBy === 'triage') return sortByTriage(kept, filter.sortDir);
  return sortByFacet(kept, sortBy, filter.sortDir);
}

/**
 * E07-F11-T02 — order by the triage bucket: «check first» → «check» → not triaged → «doubtful»
 * → «likely junk» (`asc`, the default), reversed under `desc`. Prefilter-flagged rows stay at the
 * end in both directions (spec §5.1); ties keep the score rank.
 */
function sortByTriage(rows: ReviewRow[], dir: ReviewSortDir): ReviewRow[] {
  return [...rows].sort((a, b) => {
    const flagged = Number(isPrefiltered(a)) - Number(isPrefiltered(b));
    if (flagged !== 0) return flagged;
    const byBucket = triageBucketRank(a) - triageBucketRank(b);
    if (byBucket !== 0) return dir === 'asc' ? byBucket : -byBucket;
    return (a.displayRank ?? 0) - (b.displayRank ?? 0);
  });
}

/**
 * E07-F11-T02 — once the list has triage results, the default order is «check first» on top.
 * Applied only while the person has not picked another sort (the untouched score sort). Pure.
 */
export function withDefaultTriageSort(
  filter: ReviewFilterState,
  rows: readonly ReviewRow[],
): ReviewFilterState {
  const untouched = (filter.sortBy ?? 'score') === 'score' && filter.sortDir === 'desc';
  if (!untouched || !hasTriage(rows)) return filter;
  return { ...filter, sortBy: 'triage', sortDir: 'asc' };
}

/** Flip the sort direction (DESC ↔ ASC). */
export function toggleSortDir(dir: ReviewSortDir): ReviewSortDir {
  return dir === 'desc' ? 'asc' : 'desc';
}

// ---------------------------------------------------------------------------
// T05 — pagination (a 100-440-row long-list is paged, not rendered in one go)
// ---------------------------------------------------------------------------

/** Page-size choices of the Review table; 'all' renders the whole filtered list. */
export const REVIEW_PAGE_SIZES = [50, 100, 200] as const;
export type ReviewPageSize = (typeof REVIEW_PAGE_SIZES)[number] | 'all';
export const DEFAULT_REVIEW_PAGE_SIZE: ReviewPageSize = 50;

export interface ReviewPage {
  /** The rows of the current page (a slice of the filtered + sorted list). */
  rows: ReviewRow[];
  /** 1-based page actually shown (clamped into 1..pageCount). */
  page: number;
  /** ≥ 1, even for an empty list. */
  pageCount: number;
  /** 1-based index of the first row shown (0 when the list is empty). */
  from: number;
  /** 1-based index of the last row shown (0 when the list is empty). */
  to: number;
  /** Length of the filtered list the page is cut from. */
  total: number;
}

/**
 * Cut one page out of the filtered + sorted rows. The requested page is CLAMPED
 * (a filter or an accept/reject that shrinks the list never leaves the table on a
 * non-existent page). Pagination runs AFTER ranking and filtering, so a row keeps
 * its full-list displayRank on any page. Pure.
 */
export function paginateRows(
  rows: readonly ReviewRow[],
  page: number,
  pageSize: ReviewPageSize,
): ReviewPage {
  const total = rows.length;
  if (pageSize === 'all' || total === 0) {
    return { rows: [...rows], page: 1, pageCount: 1, from: total ? 1 : 0, to: total, total };
  }
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, Math.floor(page) || 1), pageCount);
  const start = (current - 1) * pageSize;
  const slice = rows.slice(start, start + pageSize);
  return {
    rows: slice,
    page: current,
    pageCount,
    from: start + 1,
    to: start + slice.length,
    total,
  };
}

// ---------------------------------------------------------------------------
// T05 — summary strip (count badges)
// ---------------------------------------------------------------------------

export interface ReviewSummary {
  total: number; // rows on the long-list
  accepted: number;
  needsHuman: number;
  /** E07-F04-T11 — rows no AI check / Verify has touched (separate from needsHuman). */
  notChecked: number;
  discovered: number; // N discovered — the live long-list size (E07-F04-T10)
  showing: number; // rows after filtering
}

/**
 * Compute the summary strip: total on the long-list, accepted, needs-human, the
 * discovered count, and "showing" = the count after the active filter. The first
 * four come from the FULL list (never the filtered view); `showing` is the
 * filtered length. The strip reads "showing <showing> of <total> (<discovered> discovered)".
 */
export function computeSummary(
  allRows: readonly ReviewRow[],
  filteredCount: number,
  discovered: number,
): ReviewSummary {
  return {
    total: allRows.length,
    accepted: allRows.filter((r) => r.reviewStatus === 'accepted').length,
    needsHuman: allRows.filter((r) => r.needsHuman).length,
    notChecked: allRows.filter((r) => r.checkState === 'not_checked').length,
    discovered,
    showing: filteredCount,
  };
}

// ---------------------------------------------------------------------------
// T05 — breakdown legend (6 squares: Ind/Sub/Geo/Size/Fnd/Svc)
// ---------------------------------------------------------------------------

/** The legend rows for the breakdown-legend strip (re-exported for the table). */
export const REVIEW_LEGEND = REVIEW_CRITERIA_LEGEND;

/** Ordered criterion keys for the mini-breakdown squares. */
export const REVIEW_CRITERION_ORDER: readonly ReviewCriterionKey[] = REVIEW_CRITERIA_LEGEND.map(
  (l) => l.key,
);

// ---------------------------------------------------------------------------
// T06 / T05 — grade chip palette (A green / B blue / C gray; null → "—")
// ---------------------------------------------------------------------------

export interface GradeChipStyle {
  label: string;
  className: string;
  isPlaceholder: boolean;
}

const GRADE_CHIP_MAP: Record<ReviewGrade, string> = {
  A: 'bg-green-100 text-green-800 ring-green-300',
  B: 'bg-blue-100 text-blue-800 ring-blue-300',
  C: 'bg-slate-100 text-slate-600 ring-slate-300',
};

/**
 * Grade chip style (prototype gradeChip L820-828). null/undefined → the "—"
 * placeholder pill (a manual row without a score renders "—" until verified).
 */
export function gradeChipStyle(grade: ReviewGrade | null | undefined): GradeChipStyle {
  if (!grade) {
    return {
      label: '—',
      className: 'bg-slate-50 text-slate-400 ring-slate-200',
      isPlaceholder: true,
    };
  }
  return { label: grade, className: GRADE_CHIP_MAP[grade], isPlaceholder: false };
}

// ---------------------------------------------------------------------------
// T05 — verdict chip palette (pass green / partial amber / fail red)
// ---------------------------------------------------------------------------

export interface VerdictChipStyle {
  className: string;
  symbol: string; // textual marker (the React table swaps for an icon)
}

const VERDICT_CHIP_MAP: Record<ReviewStoredVerdict, VerdictChipStyle> = {
  pass: { className: 'bg-green-100 text-green-700', symbol: '✓' },
  partial: { className: 'bg-amber-100 text-amber-700', symbol: '◑' },
  fail: { className: 'bg-red-100 text-red-600', symbol: '✗' },
  // BUG-1: 'unclear' is a legitimate persisted verdict (no citable evidence
  // found — ~30% of criteria on live runs). Render it slate, mirroring the
  // Evidence-drawer's `verdictStyle` unclear palette; never let it fall back to
  // the amber "partial" chip, which would misrepresent it.
  unclear: { className: 'bg-slate-100 text-slate-500', symbol: '?' },
};

/** Verdict chip style; an unknown/missing verdict falls back to 'unclear'. */
export function verdictChipStyle(verdict: ReviewStoredVerdict | undefined): VerdictChipStyle {
  return VERDICT_CHIP_MAP[verdict ?? 'unclear'] ?? VERDICT_CHIP_MAP.unclear;
}

// ---------------------------------------------------------------------------
// T05 — row tint (amber for needs_human)
// ---------------------------------------------------------------------------

/**
 * Row background tint: needs_human → amber (prototype rowTint L892);
 * E07-F04-T11: not_checked → neutral slate (visible, but not a warning colour).
 */
export function rowTintClass(row: Pick<ReviewRow, 'needsHuman'> & { checkState?: string }): string {
  if (row.needsHuman) return 'bg-amber-50';
  return row.checkState === 'not_checked' ? 'bg-slate-50' : '';
}

// ---------------------------------------------------------------------------
// T05 — score bar width (heat-bar, prototype scoreCell L832-838)
// ---------------------------------------------------------------------------

/** Heat-bar width 6..100 for a score; null → 0 (the cell shows "—"). */
export function scoreBarWidth(score: number | null): number {
  if (score == null) return 0;
  return Math.max(6, Math.round(score));
}
